import { useState } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Button,
  Badge,
} from 'reactstrap';
import cx from 'classnames';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useUserDetail, useUserTenants } from './useUsers';
import {
  CommunicationTab,
  ContactsTab,
  SubUsersTab,
} from './BusinessAccountTabs';
import {
  useLiftSuspension,
  useAddUserTenant,
  useRevokeUserTenant,
} from './userMutations';
import { useBrand } from '../../brand/BrandProvider';
import { UserEditModal } from './UserEditModal';
import { SuspendUserDialog } from './SuspendUserDialog';
import { AnonymizeUserDialog } from './AnonymizeUserDialog';
import { VatReverifyDialog } from './VatReverifyDialog';

export function UserDetailDrawer({ userId, isOpen, onClose }) {
  const [tab, setTab] = useState('stammdaten');
  const [editOpen, setEditOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [liftOpen, setLiftOpen] = useState(false);
  const [vatOpen, setVatOpen] = useState(false);
  const [anonymizeOpen, setAnonymizeOpen] = useState(false);

  const detail = useUserDetail(userId);
  const tenants = useUserTenants(userId);
  const lift = useLiftSuspension();

  const onLift = async () => {
    if (!detail.data) return;
    await lift.mutateAsync({ id: detail.data.user.id });
    setLiftOpen(false);
  };

  return (
    <>
      <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 520 }}>
        <OffcanvasHeader toggle={onClose} className="border-bottom">
          Benutzer-Detail
        </OffcanvasHeader>
        <OffcanvasBody>
          {detail.isLoading && <QueryLoadingState />}
          {detail.isError && (
            <QueryErrorState error={detail.error} onRetry={() => detail.refetch()} />
          )}
          {detail.data && (() => {
            // Business-Account-Tabs nur bei Top-Level-Business-Org sichtbar
            // (spec: business-accounts-admin.md §Operator-Frontend).
            const user = detail.data.user;
            const isBusinessOrg =
              user.accountType === 'business' &&
              (user.parent_user_id == null && user.parentUserId == null);
            return (
            <>
              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={cx({ active: tab === 'stammdaten' })}
                    onClick={() => setTab('stammdaten')}
                    role="button"
                  >
                    Stammdaten
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={cx({ active: tab === 'tenants' })}
                    onClick={() => setTab('tenants')}
                    role="button"
                  >
                    Tenants
                    {tenants.data && (
                      <Badge color="secondary" pill className="ms-2">
                        {tenants.data.tenants.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
                {isBusinessOrg && (
                  <>
                    <NavItem>
                      <NavLink
                        className={cx({ active: tab === 'sub-users' })}
                        onClick={() => setTab('sub-users')}
                        role="button"
                      >
                        Sub-User
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={cx({ active: tab === 'contacts' })}
                        onClick={() => setTab('contacts')}
                        role="button"
                      >
                        Contacts
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={cx({ active: tab === 'communication' })}
                        onClick={() => setTab('communication')}
                        role="button"
                      >
                        Kommunikation
                      </NavLink>
                    </NavItem>
                  </>
                )}
              </Nav>

              <TabContent activeTab={tab}>
                <TabPane tabId="stammdaten">
                  <StammdatenTab
                    user={detail.data.user}
                    onEdit={() => setEditOpen(true)}
                    onSuspend={() => setSuspendOpen(true)}
                    onLift={() => setLiftOpen(true)}
                    onVat={() => setVatOpen(true)}
                    onAnonymize={() => setAnonymizeOpen(true)}
                  />
                </TabPane>
                <TabPane tabId="tenants">
                  <TenantsTab userId={userId} query={tenants} />
                </TabPane>
                {isBusinessOrg && (
                  <>
                    <TabPane tabId="sub-users">
                      <SubUsersTab userId={userId} enabled={tab === 'sub-users'} />
                    </TabPane>
                    <TabPane tabId="contacts">
                      <ContactsTab userId={userId} enabled={tab === 'contacts'} />
                    </TabPane>
                    <TabPane tabId="communication">
                      <CommunicationTab userId={userId} enabled={tab === 'communication'} />
                    </TabPane>
                  </>
                )}
              </TabContent>
            </>
            );
          })()}
        </OffcanvasBody>
      </Offcanvas>

      {detail.data && (
        <>
          {/* key={user.id} sorgt für Form-State-Reset bei User-Wechsel */}
          {editOpen && (
            <UserEditModal
              key={`edit-${detail.data.user.id}`}
              isOpen
              user={detail.data.user}
              onClose={() => setEditOpen(false)}
            />
          )}
          {suspendOpen && (
            <SuspendUserDialog
              key={`suspend-${detail.data.user.id}`}
              isOpen
              user={detail.data.user}
              onClose={() => setSuspendOpen(false)}
            />
          )}
          <ConfirmDialog
            isOpen={liftOpen}
            title="Sperre aufheben?"
            body="Der Benutzer kann sich danach wieder einloggen."
            confirmLabel="Entsperren"
            confirmColor="success"
            busy={lift.isPending}
            onConfirm={onLift}
            onCancel={() => setLiftOpen(false)}
          />
          {vatOpen && (
            <VatReverifyDialog
              key={`vat-${detail.data.user.id}`}
              user={detail.data.user}
              onClose={() => setVatOpen(false)}
            />
          )}
          {anonymizeOpen && (
            <AnonymizeUserDialog
              key={`anon-${detail.data.user.id}`}
              isOpen
              user={detail.data.user}
              onClose={() => setAnonymizeOpen(false)}
            />
          )}
        </>
      )}
    </>
  );
}

function StammdatenTab({ user, onEdit, onSuspend, onLift, onVat, onAnonymize }) {
  return (
    <>
      <div className="mb-4">
        <h5 className="mb-0">{user.displayName || '—'}</h5>
        <div className="d-flex gap-2 align-items-center mt-2 flex-wrap">
          <StatusBadge value={user.role} />
          <StatusBadge value={user.status} />
          {user.mfaEnabled && <Badge color="dark" pill>MFA</Badge>}
          {!user.emailVerified && <Badge color="warning" pill>Email unverified</Badge>}
        </div>
      </div>

      <Row className="g-3">
        <Detail label="User-ID" value={user.id} />
        <Detail label="Kundennummer" value={<CopyChip value={user.customerNumber} label="Kundennummer" />} />
        <Detail label="E-Mail" value={user.email} />
        <Detail label="Konto-Typ" value={user.accountType} />
        {user.accountType === 'business' && (
          <>
            <Detail label="Firma" value={user.companyName} />
            <Detail label="USt-IdNr" value={user.vatId ? <CopyChip value={user.vatId} label="VAT-ID" /> : '—'} />
            <Detail label="Land" value={user.billingCountry} />
            <Detail
              label="Adresse"
              value={
                user.billingStreet ? (
                  <span className="small">
                    {user.billingStreet}
                    <br />
                    {user.billingZip} {user.billingCity}
                  </span>
                ) : '—'
              }
            />
          </>
        )}
        <Detail label="Erstellt" value={<RelativeTime iso={user.createdAt} />} />
        <Detail label="Letzter Login" value={<RelativeTime iso={user.lastLoginAt} />} />
      </Row>

      <hr />

      <div className="d-flex flex-wrap gap-2">
        <HasPermission perms={['users.write']}>
          <Button color="primary" outline size="sm" onClick={onEdit} disabled={user.status === 'deleted'}>
            ✏️ Bearbeiten
          </Button>
          {user.accountType === 'business' && (
            <Button color="info" outline size="sm" onClick={onVat} disabled={user.status === 'deleted'}>
              🔁 VAT-Reverify
            </Button>
          )}
          {user.status === 'active' && (
            <Button color="warning" outline size="sm" onClick={onSuspend}>
              ⛔ Sperren
            </Button>
          )}
          {user.status === 'suspended' && (
            <Button color="success" outline size="sm" onClick={onLift}>
              ✓ Entsperren
            </Button>
          )}
        </HasPermission>
        <HasPermission perms={['users.anonymize']}>
          {user.status !== 'deleted' && (
            <Button color="danger" outline size="sm" onClick={onAnonymize}>
              🗑️ Anonymisieren
            </Button>
          )}
        </HasPermission>
      </div>
    </>
  );
}

function Detail({ label, value }) {
  return (
    <Col sm="6">
      <div className="text-uppercase small text-muted">{label}</div>
      <div className="fw-semibold">{value ?? '—'}</div>
    </Col>
  );
}

function TenantsTab({ userId, query }) {
  const brand = useBrand();
  const [adding, setAdding] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const add = useAddUserTenant();
  const revoke = useRevokeUserTenant();

  if (query.isLoading) return <QueryLoadingState />;
  if (query.isError) return <QueryErrorState error={query.error} onRetry={() => query.refetch()} />;

  const tenants = query.data?.tenants ?? [];
  const assignedOrigins = new Set(tenants.map((t) => t.brandOrigin));
  const available = brand.brands.filter((b) => !assignedOrigins.has(b.brandOrigin));

  const onAdd = async (brandOrigin) => {
    await add.mutateAsync({ id: Number(userId), brandOrigin });
    setAdding(false);
  };

  const onRevokeConfirm = async () => {
    if (!pendingRevoke) return;
    await revoke.mutateAsync({ id: Number(userId), tenantId: pendingRevoke.id });
    setPendingRevoke(null);
  };

  if (tenants.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <div className="display-1" aria-hidden>🛂</div>
        <h6 className="mt-3">Keine Brand-Zuweisung</h6>
        <p className="small mb-3">
          Dieser Mitarbeiter hat noch keine <code>user_tenants</code>-Membership.
        </p>
        <HasPermission perms={['users.write']}>
          <AddTenantPicker
            available={available}
            isOpen={adding}
            onOpen={() => setAdding(true)}
            onCancel={() => setAdding(false)}
            onPick={onAdd}
            busy={add.isPending}
          />
        </HasPermission>
      </div>
    );
  }

  return (
    <>
      <ul className="list-group list-group-flush mb-3">
        {tenants.map((t) => (
          <li
            key={t.id}
            className="list-group-item d-flex justify-content-between align-items-center px-0"
          >
            <div>
              <div className="fw-semibold">{t.displayName}</div>
              <div className="small text-muted">
                <CopyChip value={t.brandOrigin} label="Brand-Origin" />
              </div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {t.isActive ? <StatusBadge value="active" /> : <StatusBadge value="suspended" />}
              <HasPermission perms={['users.write']}>
                <Button
                  color="danger"
                  outline
                  size="sm"
                  onClick={() => setPendingRevoke(t)}
                >
                  Revoke
                </Button>
              </HasPermission>
            </div>
          </li>
        ))}
      </ul>

      <HasPermission perms={['users.write']}>
        <AddTenantPicker
          available={available}
          isOpen={adding}
          onOpen={() => setAdding(true)}
          onCancel={() => setAdding(false)}
          onPick={onAdd}
          busy={add.isPending}
        />
      </HasPermission>

      <ConfirmDialog
        isOpen={!!pendingRevoke}
        title={`Brand-Zuweisung entziehen?`}
        body={
          pendingRevoke ? (
            <>
              Mitarbeiter verliert sofort den Zugang zu{' '}
              <strong>{pendingRevoke.displayName}</strong> ({pendingRevoke.brandOrigin}).
            </>
          ) : null
        }
        confirmLabel="Revoke"
        confirmColor="danger"
        busy={revoke.isPending}
        onConfirm={onRevokeConfirm}
        onCancel={() => setPendingRevoke(null)}
      />
    </>
  );
}

function AddTenantPicker({ available, isOpen, onOpen, onCancel, onPick, busy }) {
  const [selected, setSelected] = useState('');

  if (available.length === 0) {
    return (
      <div className="text-muted small mt-2">
        Alle verfügbaren Brands sind bereits zugewiesen.
      </div>
    );
  }

  if (!isOpen) {
    return (
      <Button color="primary" size="sm" onClick={onOpen}>
        + Brand hinzufügen
      </Button>
    );
  }

  return (
    <div className="d-flex gap-2 mt-2">
      <select
        className="form-select form-select-sm"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={busy}
      >
        <option value="">Brand wählen…</option>
        {available.map((b) => (
          <option key={b.brandOrigin} value={b.brandOrigin}>
            {b.displayName}
          </option>
        ))}
      </select>
      <Button color="primary" size="sm" disabled={!selected || busy} onClick={() => onPick(selected)}>
        {busy ? '…' : 'Zuweisen'}
      </Button>
      <Button color="secondary" outline size="sm" onClick={onCancel} disabled={busy}>
        Abbrechen
      </Button>
    </div>
  );
}
